import { PlanType, Theme } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { translateAllKeyInObject } from 'utils';
import { Heading, Text, TextButton } from '@wix/design-system';
import ArrowRightSmall from 'wix-ui-icons-common/ArrowRightSmall';
import s from './PlansPageBottomBanner.scss';

const websiteBottomBannerMap = {
  [Theme.Default]: {
    title: 'plans_page.business_banner_title',
    content: 'plans_page.business_banner_subtitle',
    ctaText: 'plans_page.business_banner_textlink',
  },
  [Theme.AdvancedPlans]: {
    title: 'Advanced.Plans_Enterprise_section_title',
    content: 'Advanced.Plans_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
  [Theme.EditorX]: {
    title: 'Advanced.Plans_editorx_Enterprise_section_title',
    content: 'Advanced.Plans_editorx_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
};

const enterpriseBottomBannerMap = {
  [Theme.Default]: {
    title: 'Advanced.Plans_Enterprise_section_title',
    content: 'Advanced.Plans_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
  [Theme.AdvancedPlans]: {
    title: 'Advanced.Plans_Enterprise_section_title',
    content: 'Advanced.Plans_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
  [Theme.EditorX]: {
    title: 'Advanced.Plans_editorx_Enterprise_section_title',
    content: 'Advanced.Plans_editorx_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
  [Theme.Dayful]: {
    title: 'Advanced.Plans_editorx_Enterprise_section_title',
    content: 'Advanced.Plans_editorx_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
  [Theme.WixStudio]: {
    title: 'Advanced.Plans_Enterprise_section_title',
    content: 'Advanced.Plans_Enterprise_section_subheader',
    ctaText: 'Advanced.Plans_Enterprise_section_textlink',
  },
};

export interface PlansPageBottomBannerProps {
  onClickHandler(goToPlan: PlanType): void;
  hasEnterpriseProduct: boolean;
  rootStore: RootStore;
}

const PlansPageBottomBannerComp: React.FC<PlansPageBottomBannerProps> = ({
  onClickHandler,
  hasEnterpriseProduct,
  rootStore,
}) => {
  const { theme: themeName } = usePageModeContext();
  const { t } = useTranslation();
  const { shouldStoreIntentNoTabs, isEnterpriseTabEnabled } =
    rootStore.appStore;

  const getBannerContentKeysByPlanType = (goToPlan: PlanType) => {
    const bottomBannerMap =
      goToPlan === PlanType.Website
        ? websiteBottomBannerMap
        : enterpriseBottomBannerMap;
    return hasEnterpriseProduct
      ? translateAllKeyInObject(bottomBannerMap[themeName], t)
      : translateAllKeyInObject(bottomBannerMap[Theme.Default], t);
  };

  const isEnterpriseBanner = shouldStoreIntentNoTabs && isEnterpriseTabEnabled;
  const goToPlan = isEnterpriseBanner ? PlanType.Enterprise : PlanType.Business;
  const planType = isEnterpriseBanner ? PlanType.Enterprise : PlanType.Website;
  const onClick = () => onClickHandler(goToPlan);
  const { content, title, ctaText } = getBannerContentKeysByPlanType(planType);

  return (
    <div className={s.bannerContainer} data-hook='business-plans-banner'>
      <div className={s.innerBannerContainer}>
        <Heading dataHook='banner-title' className={s.bannerTitle}>
          {title}
        </Heading>
        <p className={s.bannerTextContainer}>
          <Text
            weight='normal'
            dataHook='banner-subtitle'
            className={s.bannerText}
          >
            {content}
          </Text>
        </p>
        <TextButton
          onClick={onClick}
          dataHook='banner-textlink'
          className={s.bannerTextlink}
          suffixIcon={<ArrowRightSmall />}
        >
          {ctaText}
        </TextButton>
      </div>
    </div>
  );
};

export const PlansPageBottomBanner = withInjectedRootStore(
  PlansPageBottomBannerComp,
);
